// src/use-snap-carousel.tsx
import { useState, useCallback, useLayoutEffect, useEffect } from "react";
var useSnapCarousel = ({
  axis = "x",
  snapPointClassName
} = {}) => {
  const dimension = axis === "x" ? "width" : "height";
  const scrollDimension = axis === "x" ? "scrollWidth" : "scrollHeight";
  const clientDimension = axis === "x" ? "clientWidth" : "clientHeight";
  const nearSidePos = axis === "x" ? "left" : "top";
  const farSidePos = axis === "x" ? "right" : "bottom";
  const scrollPos = axis === "x" ? "scrollLeft" : "scrollTop";
  const [scrollEl, setScrollEl] = useState(null);
  const [{ pages, activePageIndex }, setCarouselState] = useState({
    pages: [],
    activePageIndex: 0
  });
  const refreshActivePage = useCallback(
    (pages2) => {
      if (!scrollEl) {
        return;
      }
      const hasScrolledToEnd = Math.floor(scrollEl[scrollDimension] - scrollEl[scrollPos]) <= scrollEl[clientDimension];
      if (hasScrolledToEnd) {
        setCarouselState({ pages: pages2, activePageIndex: pages2.length - 1 });
        return;
      }
      const items = Array.from(scrollEl.children);
      const scrollPort = scrollEl.getBoundingClientRect();
      const offsets = pages2.map((page) => {
        const leadIndex = page[0];
        const leadEl = items[leadIndex];
        const rect = leadEl.getBoundingClientRect();
        const offset = rect[nearSidePos] - scrollPort[nearSidePos];
        return Math.abs(offset);
      });
      const minOffset = Math.min(...offsets);
      const nextActivePageIndex = offsets.indexOf(minOffset);
      setCarouselState({ pages: pages2, activePageIndex: nextActivePageIndex });
    },
    [scrollEl, clientDimension, nearSidePos, scrollDimension, scrollPos]
  );
  const refresh = useCallback(() => {
    if (!scrollEl) {
      return;
    }
    const items = Array.from(scrollEl.children);
    const scrollPort = scrollEl.getBoundingClientRect();
    let currPageStartPos = 0;
    const pages2 = items.reduce((acc, item, i) => {
      if (i === 0) {
        acc.push([i]);
        return acc;
      }
      const currPage = acc[acc.length - 1];
      const rect = getOffsetRect(item, item.parentElement);
      if (rect[farSidePos] - currPageStartPos > Math.ceil(scrollPort[dimension])) {
        acc.push([i]);
        currPageStartPos = rect[nearSidePos];
      } else {
        currPage.push(i);
      }
      return acc;
    }, []);
    refreshActivePage(pages2);
  }, [
    refreshActivePage,
    scrollEl,
    dimension,
    farSidePos,
    nearSidePos,
    scrollPos
  ]);
  useLayoutEffect(() => {
    refresh();
  }, [refresh]);
  useEffect(() => {
    const handle = () => {
      refresh();
    };
    window.addEventListener("resize", handle);
    window.addEventListener("orientationchange", handle);
    return () => {
      window.removeEventListener("resize", handle);
      window.removeEventListener("orientationchange", handle);
    };
  }, [refresh]);
  useEffect(() => {
    if (!scrollEl) {
      return;
    }
    const handle = () => {
      refreshActivePage(pages);
    };
    scrollEl.addEventListener("scroll", handle);
    return () => {
      scrollEl.removeEventListener("scroll", handle);
    };
  }, [refreshActivePage, pages, scrollEl]);
  const handleGoTo = (index) => {
    if (!scrollEl) {
      return;
    }
    const page = pages[index];
    if (!page) {
      return;
    }
    const items = Array.from(scrollEl.children);
    const leadIndex = page[0];
    const leadEl = items[leadIndex];
    if (!(leadEl instanceof HTMLElement)) {
      return;
    }
    scrollEl.scrollTo({
      behavior: "smooth",
      [nearSidePos]: getOffsetRect(leadEl, leadEl.parentElement)[nearSidePos]
    });
  };
  const handlePrev = () => {
    handleGoTo(activePageIndex - 1);
  };
  const handleNext = () => {
    handleGoTo(activePageIndex + 1);
  };
  useLayoutEffect(() => {
    if (!scrollEl) {
      return;
    }
    const setSnapPoint = (el) => snapPointClassName ? el.classList.add(snapPointClassName) : el.style.scrollSnapAlign = "start";
    const clearSnapPoint = (el) => snapPointClassName ? el.classList.remove(snapPointClassName) : el.style.scrollSnapAlign = "";
    const items = Array.from(scrollEl.children);
    const snapPointIndexes = new Set(pages.map((page) => page[0]));
    items.forEach((item, i) => {
      if (!(item instanceof HTMLElement)) {
        return;
      }
      if (snapPointIndexes.has(i)) {
        setSnapPoint(item);
      } else {
        clearSnapPoint(item);
      }
    });
    return () => {
      items.forEach((item) => {
        if (!(item instanceof HTMLElement)) {
          return;
        }
        clearSnapPoint(item);
      });
    };
  }, [pages, scrollEl, snapPointClassName]);
  return {
    prev: handlePrev,
    next: handleNext,
    goTo: handleGoTo,
    refresh,
    pages,
    activePageIndex,
    scrollRef: setScrollEl
  };
};
var getOffsetRect = (el, relativeTo) => {
  const rect = _getOffsetRect(el);
  if (!relativeTo) {
    return rect;
  }
  const relativeRect = _getOffsetRect(relativeTo);
  return {
    left: rect.left - relativeRect.left,
    top: rect.top - relativeRect.top,
    right: rect.right - relativeRect.left,
    bottom: rect.bottom - relativeRect.top,
    width: rect.width,
    height: rect.height
  };
};
var _getOffsetRect = (el) => {
  const rect = el.getBoundingClientRect();
  let scrollLeft = 0;
  let scrollTop = 0;
  let parentEl = el.parentElement;
  while (parentEl) {
    scrollLeft += parentEl.scrollLeft;
    scrollTop += parentEl.scrollTop;
    parentEl = parentEl.parentElement;
  }
  const left = rect.left + scrollLeft;
  const top = rect.top + scrollTop;
  return {
    left,
    top,
    right: left + rect.width,
    bottom: top + rect.height,
    width: rect.width,
    height: rect.height
  };
};
export {
  useSnapCarousel
};
